import {KeyValue} from '@angular/common';
import {Component, EventEmitter, Input, OnInit, Output, signal} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {ImageCroppedEvent} from 'ngx-image-cropper';
import {switchMap, Observable, catchError, of, filter, tap, finalize, EMPTY} from 'rxjs';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {
  ConfigService,
  Q9HtmlEncoderPipe,
  SnackBarService,
  StaticFileUploadService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {KEYS_TO_SANITIZE} from 'src/app/shared/constants/keys-to-sanitize.constants';
import {ResourceListItem} from '../../models/resource-list-item.interface';
import {ResourcesDaoService} from '../../services/resources-dao.service';
import {AddResourceDialogComponent} from '../../add-resource-dialog.component';
import {ResourceTypes} from 'src/app/shared/constants/resource-types';
import {SanitizeXssService} from 'src/app/core/services/santize-xss.service';
import {AddResourceDialogPayload} from '../../models/add-resource-dialog';

@Component({
  selector: 'app-create-resource-dialog',
  templateUrl: './create-resource-dialog.component.html',
  styleUrls: ['./create-resource-dialog.component.scss']
})
export class CreateResourceDialogComponent extends TakeUntilComponentDestroy implements OnInit {
  @Input() public name: string;
  @Input() public isArchitectural: boolean;
  @Input() public attachHidden: boolean;
  @Input() public canSwitchToList: boolean;
  @Input() public data: AddResourceDialogPayload;

  @Output() public changeHiddenFlag = new EventEmitter<boolean>();
  @Output() public switchCreateMode = new EventEmitter<void>();

  public addResourceForm: FormGroup;
  public types: typeof ResourceTypes;
  public typeOrderFn: (...args: any[]) => number;
  public createMain: boolean;
  public editIcon: boolean;
  public fileEvent = null;
  public croppedImage: Blob;
  public isLoading = signal(false);
  public error: string | null = null;

  constructor(
    private fb: FormBuilder,
    private snackBarService: SnackBarService,
    private resourceService: ResourcesDaoService,
    private staticFileUploadService: StaticFileUploadService,
    private sanitizeXssService: SanitizeXssService,
    private translate: TranslateService,
    private readonly encodeHtml: Q9HtmlEncoderPipe,
    private dialogRef: MatDialogRef<AddResourceDialogComponent>
  ) {
    super();
  }

  ngOnInit(): void {
    this.types = ResourceTypes;
    this.typeOrderFn = this.orderAsc;

    this.initForm();
  }

  public creatingMain(isMain: boolean): void {
    this.createMain = isMain;
  }

  public toggleIconEdit(val: boolean) {
    this.editIcon = val;
  }

  public fileChangeEvent(event: Event) {
    const file = (event.currentTarget as HTMLInputElement).files[0];
    const isValid = this.validateFile(file);

    if (isValid) {
      this.fileEvent = event;
    }
  }

  public imageCropped(image: ImageCroppedEvent): void {
    this.croppedImage = image.blob;
  }

  public createAndAddResource(): void {
    if (this.addResourceForm.invalid) {
      return;
    }

    this.isLoading.set(true);

    const process$ =
      this.editIcon && this.croppedImage
        ? this.uploadIcon().pipe(switchMap(cred => this.preparePayloadAndCreate(cred)))
        : this.preparePayloadAndCreate();

    process$.pipe(this.takeUntilComponentDestroy()).subscribe();
  }

  private preparePayloadAndCreate(cred?: unknown) {
    const payload = {...this.addResourceForm.value};

    payload.name = this.encodeHtml.transform(payload.name);

    if (cred) {
      payload.icon = cred[0].key;
    }

    return this.createResource(payload);
  }

  public createResource(payload): Observable<any> {
    this.sanitizeXssService.sanitizeXss(payload, KEYS_TO_SANITIZE.ATTACHMENT);

    const createUrl = `${ConfigService.environment.API_ENDPOINT}/organization/${this.data.teamId}/resource`;

    return this.resourceService.create(payload, createUrl).pipe(
      catchError(err => {
        if (err.error.status === 'access_denied') {
          this.error = this.translate.instant('CORE.RESOURCES.NO_PERMISSIONS');
        }

        if (err.error.status === 'already_exist') {
          this.addResourceForm.get('name').setErrors({duplicate: 'CORE.RESOURCES.ALREADY_EXIST'});
        }

        return EMPTY;
      }),
      filter(val => !!val),
      switchMap((res: ResourceListItem) => {
        if (this.attachHidden) {
          res.hidden = true;
        }

        if (this.createMain) {
          res.main = true;
        }

        return of([res]);
      }),
      tap(created => this.dialogRef.close({successful: true, data: created})),
      finalize(() => this.isLoading.set(false))
    );
  }

  public uploadIcon(): Observable<any> {
    const blob = this.croppedImage;
    const url = `${ConfigService.environment.API_STATIC_FILE_UPLOAD}/signedurl?actionType=resourceIcon`;
    const payload = {url: {signedurl: url}, blob, autoNotification: true};

    return this.staticFileUploadService.startUploading(payload).pipe(
      catchError(error => {
        this.isLoading.set(false);
        this.error = error.error;
        this.fileEvent = null;
        this.croppedImage = null;

        return null;
      }),
      filter(res => !!res)
    );
  }

  private validateFile(file): boolean {
    const MAX_IMAGE_SIZE = 4096; // ~ 4 mb

    if (!file) {
      return false;
    }

    const validFileType = /\.(jpe?g|png|gif)$/i.test(file.name);
    const validSize = Math.floor(file.size / 1024) < MAX_IMAGE_SIZE;

    if (!validFileType) {
      this.snackBarService.error('ASIDE_R.ERROR_SELECTED_NOT_IMAGE');

      return false;
    }

    if (!validSize) {
      this.snackBarService.error('ASIDE_R.ERROR_LARGE_IMG');

      return false;
    }

    return true;
  }

  private initForm() {
    const nameValidation = [
      Q9Validators.maxLength(60, 'CORE.RESOURCES.TITLE_LENGTH'),
      Q9Validators.minLength(2, 'CORE.RESOURCES.TITLE_LENGTH'),
      Q9Validators.required(),
      Q9Validators.pattern('^[^\'"]+$', 'CORE.RESOURCES.NAME_NOT_ALLOWED')
    ];

    this.addResourceForm = this.fb.group({
      name: [this.name, nameValidation],
      type: [ResourceTypes.Human],
      description: ['', Q9Validators.maxLength(5000, 'CORE.RESOURCES.VALUE_LENGTH')]
    });
  }

  private orderAsc = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return a.value.localeCompare(b.value);
  };
}
